<template>
  <section class="tw-bg-white tw-py-24">
    <div class="tw-container tw-mx-auto tw-px-6">
      <div class="tw-text-center tw-mb-16">
        <h2 class="tw-text-4xl md:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-4 tw-leading-tight">
          Common Questions
        </h2>
      </div>
      <div class="tw-max-w-6xl tw-mx-auto">
        <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
          <!-- FAQ Item 1 -->
          <div class="tw-bg-gray-50 tw-rounded-xl tw-border tw-border-gray-100 tw-overflow-hidden hover:tw-border-primary-200 tw-transition-colors">
            <button
              @click="toggleFaq(0)"
              class="tw-w-full tw-text-left tw-p-5 tw-flex tw-items-center tw-justify-between hover:tw-bg-gray-100/50 tw-transition-colors"
            >
              <span class="tw-font-semibold tw-text-gray-900 tw-text-sm">How long does setup take?</span>
              <svg
                :class="['tw-w-5 tw-h-5 tw-text-gray-500 tw-transition-transform tw-duration-300 tw-flex-shrink-0 tw-ml-3', { 'tw-rotate-180': openFaqs[0] }]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <transition name="faq">
              <div v-show="openFaqs[0]" class="tw-overflow-hidden">
                <div class="tw-px-5 tw-pb-5 tw-text-gray-600 tw-text-sm">
                  <p>Our standard setup takes approximately 4 weeks from start to finish. This includes configuration, integration, and testing.</p>
                </div>
              </div>
            </transition>
          </div>

          <!-- FAQ Item 2 -->
          <div class="tw-bg-gray-50 tw-rounded-xl tw-border tw-border-gray-100 tw-overflow-hidden hover:tw-border-primary-200 tw-transition-colors">
            <button
              @click="toggleFaq(1)"
              class="tw-w-full tw-text-left tw-p-5 tw-flex tw-items-center tw-justify-between hover:tw-bg-gray-100/50 tw-transition-colors"
            >
              <span class="tw-font-semibold tw-text-gray-900 tw-text-sm">Can we integrate with our existing systems?</span>
              <svg
                :class="['tw-w-5 tw-h-5 tw-text-gray-500 tw-transition-transform tw-duration-300 tw-flex-shrink-0 tw-ml-3', { 'tw-rotate-180': openFaqs[1] }]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <transition name="faq">
              <div v-show="openFaqs[1]" class="tw-overflow-hidden">
                <div class="tw-px-5 tw-pb-5 tw-text-gray-600 tw-text-sm">
                  <p>Yes, we offer API integration and can work with most event management systems, CRM platforms, and registration tools.</p>
                </div>
              </div>
            </transition>
          </div>

          <!-- FAQ Item 3 -->
          <div class="tw-bg-gray-50 tw-rounded-xl tw-border tw-border-gray-100 tw-overflow-hidden hover:tw-border-primary-200 tw-transition-colors">
            <button
              @click="toggleFaq(2)"
              class="tw-w-full tw-text-left tw-p-5 tw-flex tw-items-center tw-justify-between hover:tw-bg-gray-100/50 tw-transition-colors"
            >
              <span class="tw-font-semibold tw-text-gray-900 tw-text-sm">What's the typical cost?</span>
              <svg
                :class="['tw-w-5 tw-h-5 tw-text-gray-500 tw-transition-transform tw-duration-300 tw-flex-shrink-0 tw-ml-3', { 'tw-rotate-180': openFaqs[2] }]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <transition name="faq">
              <div v-show="openFaqs[2]" class="tw-overflow-hidden">
                <div class="tw-px-5 tw-pb-5 tw-text-gray-600 tw-text-sm">
                  <p>Pricing is customized based on your event size and requirements. Most organizers invest between $15K–30K for a complete solution.</p>
                </div>
              </div>
            </transition>
          </div>

          <!-- FAQ Item 4 -->
          <div class="tw-bg-gray-50 tw-rounded-xl tw-border tw-border-gray-100 tw-overflow-hidden hover:tw-border-primary-200 tw-transition-colors">
            <button
              @click="toggleFaq(3)"
              class="tw-w-full tw-text-left tw-p-5 tw-flex tw-items-center tw-justify-between hover:tw-bg-gray-100/50 tw-transition-colors"
            >
              <span class="tw-font-semibold tw-text-gray-900 tw-text-sm">How many attendees can the platform handle?</span>
              <svg
                :class="['tw-w-5 tw-h-5 tw-text-gray-500 tw-transition-transform tw-duration-300 tw-flex-shrink-0 tw-ml-3', { 'tw-rotate-180': openFaqs[3] }]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <transition name="faq">
              <div v-show="openFaqs[3]" class="tw-overflow-hidden">
                <div class="tw-px-5 tw-pb-5 tw-text-gray-600 tw-text-sm">
                  <p>Our platform scales from 200 to 5,000+ attendees. We've successfully managed events of all sizes across multiple regions.</p>
                </div>
              </div>
            </transition>
          </div>

          <!-- FAQ Item 5 -->
          <div class="tw-bg-gray-50 tw-rounded-xl tw-border tw-border-gray-100 tw-overflow-hidden hover:tw-border-primary-200 tw-transition-colors">
            <button
              @click="toggleFaq(4)"
              class="tw-w-full tw-text-left tw-p-5 tw-flex tw-items-center tw-justify-between hover:tw-bg-gray-100/50 tw-transition-colors"
            >
              <span class="tw-font-semibold tw-text-gray-900 tw-text-sm">Can we white-label the platform?</span>
              <svg
                :class="['tw-w-5 tw-h-5 tw-text-gray-500 tw-transition-transform tw-duration-300 tw-flex-shrink-0 tw-ml-3', { 'tw-rotate-180': openFaqs[4] }]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <transition name="faq">
              <div v-show="openFaqs[4]" class="tw-overflow-hidden">
                <div class="tw-px-5 tw-pb-5 tw-text-gray-600 tw-text-sm">
                  <p>Absolutely! White-label customization is included. We'll match your brand colors, logo, and design preferences.</p>
                </div>
              </div>
            </transition>
          </div>

          <!-- FAQ Item 6 -->
          <div class="tw-bg-gray-50 tw-rounded-xl tw-border tw-border-gray-100 tw-overflow-hidden hover:tw-border-primary-200 tw-transition-colors">
            <button
              @click="toggleFaq(5)"
              class="tw-w-full tw-text-left tw-p-5 tw-flex tw-items-center tw-justify-between hover:tw-bg-gray-100/50 tw-transition-colors"
            >
              <span class="tw-font-semibold tw-text-gray-900 tw-text-sm">What kind of support do you provide?</span>
              <svg
                :class="['tw-w-5 tw-h-5 tw-text-gray-500 tw-transition-transform tw-duration-300 tw-flex-shrink-0 tw-ml-3', { 'tw-rotate-180': openFaqs[5] }]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <transition name="faq">
              <div v-show="openFaqs[5]" class="tw-overflow-hidden">
                <div class="tw-px-5 tw-pb-5 tw-text-gray-600 tw-text-sm">
                  <p>We provide dedicated support throughout your event lifecycle, including setup assistance, live event support, and post-event consultation.</p>
                </div>
              </div>
            </transition>
          </div>

          <!-- FAQ Item 7 -->
          <div class="tw-bg-gray-50 tw-rounded-xl tw-border tw-border-gray-100 tw-overflow-hidden hover:tw-border-primary-200 tw-transition-colors">
            <button
              @click="toggleFaq(6)"
              class="tw-w-full tw-text-left tw-p-5 tw-flex tw-items-center tw-justify-between hover:tw-bg-gray-100/50 tw-transition-colors"
            >
              <span class="tw-font-semibold tw-text-gray-900 tw-text-sm">How does the matching algorithm work?</span>
              <svg
                :class="['tw-w-5 tw-h-5 tw-text-gray-500 tw-transition-transform tw-duration-300 tw-flex-shrink-0 tw-ml-3', { 'tw-rotate-180': openFaqs[6] }]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <transition name="faq">
              <div v-show="openFaqs[6]" class="tw-overflow-hidden">
                <div class="tw-px-5 tw-pb-5 tw-text-gray-600 tw-text-sm">
                  <p>Our AI-powered algorithm analyzes attendee profiles, business needs, industry sectors, and meeting preferences to create optimal B2B matches.</p>
                </div>
              </div>
            </transition>
          </div>

          <!-- FAQ Item 8 -->
          <div class="tw-bg-gray-50 tw-rounded-xl tw-border tw-border-gray-100 tw-overflow-hidden hover:tw-border-primary-200 tw-transition-colors">
            <button
              @click="toggleFaq(7)"
              class="tw-w-full tw-text-left tw-p-5 tw-flex tw-items-center tw-justify-between hover:tw-bg-gray-100/50 tw-transition-colors"
            >
              <span class="tw-font-semibold tw-text-gray-900 tw-text-sm">What analytics are included?</span>
              <svg
                :class="['tw-w-5 tw-h-5 tw-text-gray-500 tw-transition-transform tw-duration-300 tw-flex-shrink-0 tw-ml-3', { 'tw-rotate-180': openFaqs[7] }]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <transition name="faq">
              <div v-show="openFaqs[7]" class="tw-overflow-hidden">
                <div class="tw-px-5 tw-pb-5 tw-text-gray-600 tw-text-sm">
                  <p>You'll get real-time dashboards, meeting completion rates, attendee engagement metrics, sponsor ROI data, and comprehensive post-event reports.</p>
                </div>
              </div>
            </transition>
          </div>
        </div>
        <div class="tw-text-center tw-mt-10">
          <p class="tw-text-gray-600 tw-mb-4">Have another question?</p>
          <button class="tw-text-primary-600 tw-font-semibold hover:tw-text-primary-700 tw-transition-colors">
            Let's chat →
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { defineProps } from 'vue';

defineProps({
  openFaqs: {
    type: Array,
    required: true
  },
  toggleFaq: {
    type: Function,
    required: true
  }
})
</script>

<style scoped>
.faq-enter-active {
  transition: all 0.3s ease-out;
}

.faq-leave-active {
  transition: all 0.3s ease-in;
}

.faq-enter-from {
  max-height: 0;
  opacity: 0;
  transform: translateY(-10px);
}

.faq-enter-to {
  max-height: 500px;
  opacity: 1;
  transform: translateY(0);
}

.faq-leave-from {
  max-height: 500px;
  opacity: 1;
  transform: translateY(0);
}

.faq-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-10px);
}
</style>
